import { createClient } from '@connectrpc/connect';
import { SharedParameterService } from './gen/shared_parameter_service_pb';
import { pollRevitOperation, type RevitOperationProgressSnapshot } from './operationProgressClient';
import { getGrpcTransport } from './transport';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { mapProjectUndoEntries, toProjectUndoRequestEntry } from './parameterUndoMappers';
import type { ConnectorProjectParameterUndoEntry } from '../../types/parameterSetupUndo';

function getClient() {
  const transport = getGrpcTransport();
  if (!transport) {
    return null;
  }

  return createClient(SharedParameterService, transport);
}

export interface ExpectedDocumentIdentity {
  expectedDocumentName?: string;
  expectedDocumentPath?: string;
}

export { addProjectParametersGrpc } from './projectParameterClient';

export async function getSharedParametersGrpc(): Promise<{
  success: boolean;
  parameters?: Array<{
    name: string;
    guid: string;
    group: string;
    dataType: string;
    description: string;
    visible: boolean;
  }>;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetSharedParameters: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.getSharedParameters({});
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetSharedParameters: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      parameters: response.parameters.map(p => ({
        name: p.name,
        guid: p.guid,
        group: p.group,
        dataType: p.dataType,
        description: p.description,
        visible: p.visible,
      })),
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetSharedParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetSharedParameters: ${message}`);
    return { success: false, error: message };
  }
}

export async function loadSharedParameterFileGrpc(
  filePath?: string,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  filePath?: string;
  parameterCount?: number;
  parameters?: Array<{
    name: string;
    guid: string;
    group: string;
    dataType: string;
    parameterType: string;
    description: string;
    visible: boolean;
  }>;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('LoadSharedParameterFile: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const request = { filePath: filePath ?? '' };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startLoadSharedParameterFile(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseLoadSharedParameterFileResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'LoadSharedParameterFile operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`LoadSharedParameterFile: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch {
        // Older connector builds only expose the unary method.
      }
    }

    const response = await client.loadSharedParameterFile(request);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`LoadSharedParameterFile: ${response.error}`);
    }
    return mapLoadSharedParameterFileResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'LoadSharedParameterFile gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`LoadSharedParameterFile: ${message}`);
    return { success: false, error: message };
  }
}

export async function createTypeParametersGrpc(
  parameterNames: string[],
  categories: string[],
  sharedParamFile?: string,
  expectedDocumentIdentity?: ExpectedDocumentIdentity,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  successCount?: number;
  failureCount?: number;
  totalCount?: number;
  results?: Array<{
    parameter: string;
    success: boolean;
    message: string;
    error: string;
    guid: string;
    categories: string[];
  }>;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('CreateTypeParameters: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const request = {
      parameterNames,
      categories,
      sharedParamFile: sharedParamFile ?? '',
      expectedDocumentName: expectedDocumentIdentity?.expectedDocumentName ?? '',
      expectedDocumentPath: expectedDocumentIdentity?.expectedDocumentPath ?? '',
    };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startCreateTypeParameters(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseParameterCreationResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'CreateTypeParameters operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CreateTypeParameters: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch {
        // Older connector builds only expose the unary method.
      }
    }

    const response = await client.createTypeParameters(request);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CreateTypeParameters: ${response.error}`);
    }
    return mapParameterCreationResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'CreateTypeParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CreateTypeParameters: ${message}`);
    return { success: false, error: message };
  }
}

export async function addParametersToAllFamiliesGrpc(
  payloadJson: string,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  modifiedFamiliesCount?: number;
  modifiedFamilies?: Array<{
    familyName: string;
    familyId: string;
    category: string;
    addedParameters: string[];
  }>;
  message?: string;
  status?: string;
  requestId?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('AddParametersToAllFamilies: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const request = { payloadJson };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startAddParametersToAllFamilies(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseAddParametersToAllFamiliesResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'AddParametersToAllFamilies operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddParametersToAllFamilies: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch {
        // Older connector builds only expose the unary method.
      }
    }

    const response = await client.addParametersToAllFamilies(request);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddParametersToAllFamilies: ${response.error}`);
    }
    return mapAddParametersToAllFamiliesResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'AddParametersToAllFamilies gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddParametersToAllFamilies: ${message}`);
    return { success: false, error: message };
  }
}

export async function deleteAndReAddParameterGrpc(
  oldParamName: string,
  newParamName: string,
  categories: string[],
  expectedDocumentIdentity?: ExpectedDocumentIdentity,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  oldParameter?: string;
  newParameter?: string;
  categories?: string[];
  warnings?: string[];
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('DeleteAndReAddParameter: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const request = {
      oldParamName,
      newParamName,
      categories,
      expectedDocumentName: expectedDocumentIdentity?.expectedDocumentName ?? '',
      expectedDocumentPath: expectedDocumentIdentity?.expectedDocumentPath ?? '',
    };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startDeleteAndReAddParameter(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseDeleteAndReAddParameterResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'DeleteAndReAddParameter operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`DeleteAndReAddParameter: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch {
        // Older connector builds only expose the unary method.
      }
    }

    const response = await client.deleteAndReAddParameter(request);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`DeleteAndReAddParameter: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      oldParameter: response.oldParameter || undefined,
      newParameter: response.newParameter || undefined,
      categories: response.categories || undefined,
      warnings: response.warnings || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'DeleteAndReAddParameter gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`DeleteAndReAddParameter: ${message}`);
    return { success: false, error: message };
  }
}

function parseDeleteAndReAddParameterResultJson(resultJson: string): {
  success: boolean;
  oldParameter?: string;
  newParameter?: string;
  categories?: string[];
  warnings?: string[];
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    oldParameter: parsed.oldParameter || undefined,
    newParameter: parsed.newParameter || undefined,
    categories: Array.isArray(parsed.categories) ? parsed.categories : undefined,
    warnings: Array.isArray(parsed.warnings) ? parsed.warnings : undefined,
    error: parsed.error || undefined,
  };
}

function parseAddParametersToAllFamiliesResultJson(resultJson: string): {
  success: boolean;
  modifiedFamiliesCount?: number;
  modifiedFamilies?: Array<{
    familyName: string;
    familyId: string;
    category: string;
    addedParameters: string[];
  }>;
  message?: string;
  status?: string;
  requestId?: string;
  error?: string;
} {
  return mapAddParametersToAllFamiliesResponse(JSON.parse(resultJson));
}

function mapAddParametersToAllFamiliesResponse(response: any): {
  success: boolean;
  modifiedFamiliesCount?: number;
  modifiedFamilies?: Array<{
    familyName: string;
    familyId: string;
    category: string;
    addedParameters: string[];
  }>;
  message?: string;
  status?: string;
  requestId?: string;
  error?: string;
} {
  return {
    success: Boolean(response.isSuccess),
    modifiedFamiliesCount: response.modifiedFamiliesCount || undefined,
    modifiedFamilies: Array.isArray(response.modifiedFamilies)
      ? response.modifiedFamilies.map((f: any) => ({
          familyName: f.familyName,
          familyId: f.familyId,
          category: f.category,
          addedParameters: Array.isArray(f.addedParameters) ? f.addedParameters : [],
        }))
      : undefined,
    message: response.message || undefined,
    status: response.status || undefined,
    requestId: response.requestId || undefined,
    error: response.error || undefined,
  };
}

function parseParameterCreationResultJson(resultJson: string): {
  success: boolean;
  successCount?: number;
  failureCount?: number;
  totalCount?: number;
  results?: Array<{
    parameter: string;
    success: boolean;
    message: string;
    error: string;
    guid: string;
    categories: string[];
  }>;
  error?: string;
} {
  return mapParameterCreationResponse(JSON.parse(resultJson));
}

function mapParameterCreationResponse(response: any): {
  success: boolean;
  successCount?: number;
  failureCount?: number;
  totalCount?: number;
  results?: Array<{
    parameter: string;
    success: boolean;
    message: string;
    error: string;
    guid: string;
    categories: string[];
  }>;
  error?: string;
} {
  return {
    success: Boolean(response.isSuccess),
    successCount: response.successCount || undefined,
    failureCount: response.failureCount || undefined,
    totalCount: response.totalCount || undefined,
    results: Array.isArray(response.results)
      ? response.results.map((r: any) => ({
          parameter: r.parameter,
          success: Boolean(r.success),
          message: r.message,
          error: r.error,
          guid: r.guid,
          categories: Array.isArray(r.categories) ? r.categories : [],
        }))
      : undefined,
    error: response.error || undefined,
  };
}

function parseLoadSharedParameterFileResultJson(resultJson: string): {
  success: boolean;
  filePath?: string;
  parameterCount?: number;
  parameters?: Array<{
    name: string;
    guid: string;
    group: string;
    dataType: string;
    parameterType: string;
    description: string;
    visible: boolean;
  }>;
  error?: string;
} {
  return mapLoadSharedParameterFileResponse(JSON.parse(resultJson));
}

function mapLoadSharedParameterFileResponse(response: any): {
  success: boolean;
  filePath?: string;
  parameterCount?: number;
  parameters?: Array<{
    name: string;
    guid: string;
    group: string;
    dataType: string;
    parameterType: string;
    description: string;
    visible: boolean;
  }>;
  error?: string;
} {
  return {
    success: Boolean(response.isSuccess),
    filePath: response.filePath || undefined,
    parameterCount: response.parameterCount || undefined,
    parameters: Array.isArray(response.parameters)
      ? response.parameters.map((p: any) => ({
          name: p.name,
          guid: p.guid,
          group: p.group,
          dataType: p.dataType,
          parameterType: p.parameterType,
          description: p.description,
          visible: Boolean(p.visible),
        }))
      : undefined,
    error: response.error || undefined,
  };
}

export async function undoAddedProjectParametersGrpc(
  parameterNames: string[],
  expectedDocumentIdentity?: ExpectedDocumentIdentity,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void; entries?: ConnectorProjectParameterUndoEntry[] }
): Promise<{
  success: boolean; removedParameterCount?: number; restoredParameterCount?: number;
  errors?: string[]; message?: string; error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('UndoAddedProjectParameters: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const undoRequest = {
      parameterNames,
      expectedDocumentName: expectedDocumentIdentity?.expectedDocumentName ?? '',
      expectedDocumentPath: expectedDocumentIdentity?.expectedDocumentPath ?? '',
      entries: (options?.entries ?? []).map(toProjectUndoRequestEntry),
    };

    try {
      const startResponse = await client.startUndoAddedProjectParameters(undoRequest);
      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          onProgress: options?.onProgress,
          parseResult: parseUndoAddedProjectParametersResultJson,
        });

        if (!operationResult.success || !operationResult.result) {
          const error = operationResult.error || 'UndoAddedProjectParameters operation failed';
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UndoAddedProjectParameters: ${error}`);
          return { success: false, error };
        }

        if (operationResult.result.success) {
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
        } else if (operationResult.result.error) {
          useIntegrationDiagnosticsStore
            .getState()
            .setLastGrpcError(`UndoAddedProjectParameters: ${operationResult.result.error}`);
        }

        return operationResult.result;
      }
    } catch {
      // Older connector builds only expose the unary method.
    }

    const response = await client.undoAddedProjectParameters(undoRequest);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UndoAddedProjectParameters: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      removedParameterCount: response.removedParameterCount || undefined, restoredParameterCount: response.restoredParameterCount || undefined,
      errors: response.errors.length > 0 ? response.errors : undefined,
      message: response.message || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'UndoAddedProjectParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UndoAddedProjectParameters: ${message}`);
    return { success: false, error: message };
  }
}

function parseUndoAddedProjectParametersResultJson(resultJson: string): {
  success: boolean; removedParameterCount?: number; restoredParameterCount?: number;
  errors?: string[]; message?: string; error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    removedParameterCount: parsed.removedParameterCount || undefined, restoredParameterCount: parsed.restoredParameterCount || undefined,
    errors: Array.isArray(parsed.errors) && parsed.errors.length > 0 ? parsed.errors : undefined,
    message: parsed.message || undefined,
    error: parsed.error || undefined,
  };
}

type ParameterCreationTerminalStatus =
  | 'Created'
  | 'Updated'
  | 'AlreadyExistsCompatible'
  | 'Conflict'
  | 'Failed';

function normalizeParameterCreationTerminalStatus(
  value: unknown
): ParameterCreationTerminalStatus | undefined {
  switch (value) {
    case 'Created':
    case 'Updated':
    case 'AlreadyExistsCompatible':
    case 'Conflict':
    case 'Failed':
      return value;
    default:
      return undefined;
  }
}

export async function createParametersFromParsedFileGrpc(
  parameters: Array<{ name: string; guid: string; dataType: string; groupId: number; description: string }>,
  categories: string[],
  isType: boolean,
  expectedDocumentIdentity?: ExpectedDocumentIdentity,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  successCount?: number;
  failureCount?: number;
  totalCount?: number;
  results?: Array<{
    parameter: string;
    status?: ParameterCreationTerminalStatus;
    success: boolean;
    message: string;
    error: string;
    guid: string;
    categories: string[];
  }>;
  error?: string; undoEntries?: ConnectorProjectParameterUndoEntry[];
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('CreateParametersFromParsedFile: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    if (options?.onProgress) {
      const startResponse = await client.startCreateParametersFromParsedFile({
        parameters,
        categories,
        isType,
        expectedDocumentName: expectedDocumentIdentity?.expectedDocumentName ?? '',
        expectedDocumentPath: expectedDocumentIdentity?.expectedDocumentPath ?? '',
      });

      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          onProgress: options?.onProgress,
          parseResult: parseCreateParametersFromParsedFileResultJson,
        });

        if (!operationResult.result) {
          const error = operationResult.error || 'CreateParametersFromParsedFile operation failed';
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CreateParametersFromParsedFile: ${error}`);
          return { success: false, error };
        }

        if (operationResult.result.success) {
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
        } else if (operationResult.result.error) {
          useIntegrationDiagnosticsStore
            .getState()
            .setLastGrpcError(`CreateParametersFromParsedFile: ${operationResult.result.error}`);
        }

        return operationResult.result;
      }

      return {
        success: false,
        error: startResponse.error || 'CreateParametersFromParsedFile did not start a progress operation',
      };
    }

    const response = await client.createParametersFromParsedFile({
      parameters,
      categories,
      isType,
      expectedDocumentName: expectedDocumentIdentity?.expectedDocumentName ?? '',
      expectedDocumentPath: expectedDocumentIdentity?.expectedDocumentPath ?? '',
    });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CreateParametersFromParsedFile: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      successCount: response.successCount || undefined,
      failureCount: response.failureCount || undefined,
      totalCount: response.totalCount || undefined,
      results: response.results.map(r => ({
        parameter: r.parameter,
        status: normalizeParameterCreationTerminalStatus(r.status),
        success: r.success,
        message: r.message,
        error: r.error,
        guid: r.guid,
        categories: r.categories,
      })),
      error: response.error || undefined,
      undoEntries: mapProjectUndoEntries(response.undoEntries),
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'CreateParametersFromParsedFile gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CreateParametersFromParsedFile: ${message}`);
    return { success: false, error: message };
  }
}

function parseCreateParametersFromParsedFileResultJson(resultJson: string): {
  success: boolean;
  successCount?: number;
  failureCount?: number;
  totalCount?: number;
  results?: Array<{
    parameter: string;
    status?: ParameterCreationTerminalStatus;
    success: boolean;
    message: string;
    error: string;
    guid: string;
    categories: string[];
  }>;
  error?: string; undoEntries?: ConnectorProjectParameterUndoEntry[];
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    successCount: parsed.successCount || undefined,
    failureCount: parsed.failureCount || undefined,
    totalCount: parsed.totalCount || undefined,
    results: Array.isArray(parsed.results)
      ? parsed.results.map((result: Record<string, unknown>) => ({
          parameter: typeof result.parameter === 'string' ? result.parameter : '',
          status: normalizeParameterCreationTerminalStatus(result.status),
          success: result.success === true,
          message: typeof result.message === 'string' ? result.message : '',
          error: typeof result.error === 'string' ? result.error : '',
          guid: typeof result.guid === 'string' ? result.guid : '',
          categories: Array.isArray(result.categories)
            ? result.categories.filter((category): category is string => typeof category === 'string')
            : [],
        }))
      : [],
    error: parsed.error || undefined,
    undoEntries: mapProjectUndoEntries(parsed.undoEntries),
  };
}
